import { useEffect, useState } from 'react'
import { useGetAccountQuery } from '../../../../../store/accountUserSettings/accountUserSettings'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../../store/hooks'
import { CheckpointRequest, useCreateCheckpointMutation, useGetCheckpointQuery, useUpdateCheckpointMutation } from '../../../../../store/sync/sync'
import { useGetMeQuery, useUpdateUserIntegrationMutation } from '../../../../../store/user/user'
import { MultiSelectDropdown, MultiSelectOption } from '../../../../../components/MultiSelectDropdown/MultiSelectDropdown'
import { RedtailInboundContactSyncSettingsFilters, redtailSettingsDefaultValues, RedtailSettingsFormValues } from './model'
import { Controller, useForm } from 'react-hook-form'
import { IntegrationServiceId } from '../../../../../common/integrations'
import { useGetIntegrationEntitiesQuery } from '../../../../../store/integrations/integrations'
import { getAccountCRMIntegrationConfig } from '../../../../../models/Account'
import { merge } from '../../../utils'
import { removeEmptyItems } from '../../../../../common/util'
import { Col, Container, Form, Row, Stack } from 'react-bootstrap'
import { ScopedBlock } from '../../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../../components/ScopedBlock/models'
import { FormFeedback } from '../../../../../components/Form/FormFeedback'
import { Button } from '../../../../../components/Button/Button'

export const RedtailContactSyncSettings = () => {
  const accountId = useAppSelector(selectAccountId)
  const { data: account, isFetching: isAccountFetching } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: 10 })
  const checkpointRequest = {
    accountId,
    level: 'user',
    connector: 'redtail',
    direction: 'inbound',
    object: 'contact_operation_hashed',
  } as CheckpointRequest
  const { data: checkpoint, isFetching: isGetCheckpointFetching } = useGetCheckpointQuery(checkpointRequest)
  const [createCheckpoint, { isLoading: isCreateCheckpointLoading }] = useCreateCheckpointMutation()
  const [updateCheckpoint, { isLoading: isUpdateCheckpointLoading }] = useUpdateCheckpointMutation()

  const [isSyncDisabled, setIsSyncDisabled] = useState<boolean>(true)
  const { data: userData, isFetching: isGetMeQueryFetching } = useGetMeQuery({ accountId: accountId })
  const [updateUserIntegration, { isLoading: isUpdateUserIntegrationLoading }] = useUpdateUserIntegrationMutation()

  const [isUpdating, setIsUpdating] = useState<boolean>(false)

  const [contactCategoryOptions, setContactCategoryOptions] = useState<MultiSelectOption[] | null>(null)
  const [contactStatusOptions, setContactStatusOptions] = useState<MultiSelectOption[] | null>(null)
  const [contactTypeOptions, setContactTypeOptions] = useState<MultiSelectOption[] | null>(null)
  const [tierCategoriesSelectOptions, setTierCategoriesSelectOptions] = useState<MultiSelectOption[] | null>(null)

  const form = useForm<RedtailSettingsFormValues>({ defaultValues: redtailSettingsDefaultValues })

  const { data: contactCategoriesEntity, isFetching: isContactCategoriesFetching } = useGetIntegrationEntitiesQuery({
    accountId,
    connector: IntegrationServiceId.REDTAIL,
    entityType: 'contact_categories',
  })

  const { data: contactStatusesEntity, isFetching: isContactStatusesFetching } = useGetIntegrationEntitiesQuery({
    accountId,
    connector: IntegrationServiceId.REDTAIL,
    entityType: 'contact_statuses',
  })

  const { data: contactTypesEntity, isFetching: isContactTypesFetching } = useGetIntegrationEntitiesQuery({
    accountId,
    connector: IntegrationServiceId.REDTAIL,
    entityType: 'contact_types',
  })

  useEffect(() => {
    if (account && account.data) {
      const serviceIntegration = getAccountCRMIntegrationConfig(account.data, 'redtail')
      setIsSyncDisabled(serviceIntegration?.isSyncDisabled ?? false)
    }
  }, [account])

  useEffect(() => {
    if (!contactCategoriesEntity) {
      return
    }
    const { contactCategories } = contactCategoriesEntity.data
    if (!contactCategories) {
      return
    }
    const options = contactCategories.map((e: { id: number; name: string }) => ({
      id: `${e.id}`,
      label: e.name,
    }))
    setContactCategoryOptions(options)
    setTierCategoriesSelectOptions(options)
  }, [contactCategoriesEntity])

  useEffect(() => {
    if (!contactStatusesEntity) {
      return
    }
    const { contactStatuses } = contactStatusesEntity.data
    if (!contactStatuses) {
      return
    }
    const options = contactStatuses.map((e: { id: number; name: string }) => ({
      id: `${e.id}`,
      label: e.name,
    }))
    setContactStatusOptions(options)
  }, [contactStatusesEntity])

  useEffect(() => {
    if (!contactTypesEntity) {
      return
    }
    const { contactTypes } = contactTypesEntity.data
    if (!contactTypes) {
      return
    }
    const options = contactTypes.map((e: string) => ({ id: e.toLowerCase(), label: e }))
    setContactTypeOptions(options)
  }, [contactTypesEntity])

  useEffect(() => {
    if (isGetCheckpointFetching || !checkpoint) {
      return
    }
    form.setValue('inbound.contact.autoSync', checkpoint.data?.active || false)
  }, [isGetCheckpointFetching, checkpoint, form])

  useEffect(() => {
    if (isGetMeQueryFetching || !userData) {
      return
    }
    const integration = userData.integrations?.find((i) => i.serviceId === IntegrationServiceId.REDTAIL)
    if (!integration) {
      return
    }
    if (integration.serviceConfig?.inbound?.contact?.filters) {
      form.setValue('inbound.contact.filters', integration.serviceConfig.inbound.contact.filters)
    }
    if (integration.serviceConfig?.inbound?.contact?.tierCategories) {
      form.setValue('inbound.contact.tierCategories', integration.serviceConfig.inbound.contact.tierCategories)
    }
  }, [isGetMeQueryFetching, userData, form])

  const onSubmit = async (data: RedtailSettingsFormValues) => {
    try {
      setIsUpdating(true)
      if (!checkpoint?.data) {
        if (data.inbound.contact.autoSync) {
          await createCheckpoint(checkpointRequest)
        }
      } else {
        await updateCheckpoint({ ...checkpointRequest, active: data.inbound.contact.autoSync })
      }
      const integration = userData?.integrations?.find((i) => i.serviceId === IntegrationServiceId.REDTAIL)
      if (integration) {
        const filters: RedtailInboundContactSyncSettingsFilters = {
          type: (data.inbound.contact.filters.type ?? []).length > 0 ? data.inbound.contact.filters.type : undefined,
          status_id: (data.inbound.contact.filters.status_id ?? []).length > 0 ? data.inbound.contact.filters.status_id : undefined,
          category_id: (data.inbound.contact.filters.category_id ?? []).length > 0 ? data.inbound.contact.filters.category_id : undefined,
        }
        const tierCategories = (data.inbound.contact.tierCategories ?? []).length > 0 ? data.inbound.contact.tierCategories : null

        const serviceConfig = merge(integration.serviceConfig || {}, { inbound: { contact: { filters, tierCategories } } })

        await updateUserIntegration({
          accountId,
          integrationName: integration.serviceId,
          integrationId: integration.id,
          payload: { serviceConfig: removeEmptyItems(serviceConfig) },
        })
      }
    } catch (e) {
      console.error(e)
    } finally {
      setIsUpdating(false)
    }
  }

  const handleCancel = () => {
    form.reset()
  }

  if (!isUpdating && (isGetMeQueryFetching || isGetCheckpointFetching || isContactCategoriesFetching || isContactTypesFetching || isContactStatusesFetching)) {
    return (
      <Container>
        <div className='skeleton mb-2' style={{ width: '100%', height: '70px' }} />
        <div className='skeleton mb-3' style={{ width: '100%', height: '70px' }} />
      </Container>
    )
  }

  return (
    <Container>
      <h6>Contacts</h6>
      <div>Keep your contacts in sync between Zocks and Redtail.</div>
      <Row className='mb-1 mt-3'>
        <Col>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <div>Synchronize your contacts automatically</div>
          </Stack>
        </Col>
        <Col md='auto'>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <Controller
              name='inbound.contact.autoSync'
              control={form.control}
              render={({ field, fieldState }) => (
                <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
                  <Form.Switch
                    checked={field.value === true}
                    onChange={field.onChange}
                    disabled={isGetCheckpointFetching || isSyncDisabled || isAccountFetching}
                  />
                  {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                </ScopedBlock>
              )}
            />
          </Stack>
        </Col>
      </Row>
      <hr></hr>
      <div className={'label-large'}>Filters</div>
      <Row className='mb-1 mt-3'>
        <Col className='d-flex align-items-center'>
          <div>Status</div>
        </Col>
        <Col>
          {contactStatusOptions ? (
            <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
              <Controller
                name='inbound.contact.filters.status_id'
                control={form.control}
                render={({ field }) => (
                  <MultiSelectDropdown
                    name={field.name}
                    options={contactStatusOptions}
                    onSelectedChanged={field.onChange}
                    initialSelectedOptions={field.value ?? []}
                  />
                )}
              />
            </ScopedBlock>
          ) : null}
        </Col>
      </Row>
      <Row className='mb-1 mt-3'>
        <Col className='d-flex align-items-center'>
          <div>Type</div>
        </Col>
        <Col>
          {contactTypeOptions ? (
            <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
              <Controller
                name='inbound.contact.filters.type'
                control={form.control}
                render={({ field }) => (
                  <MultiSelectDropdown
                    name={field.name}
                    options={contactTypeOptions}
                    onSelectedChanged={field.onChange}
                    initialSelectedOptions={field.value ?? []}
                  />
                )}
              />
            </ScopedBlock>
          ) : null}
        </Col>
      </Row>
      <Row className='mb-1 mt-3'>
        <Col className='d-flex align-items-center'>
          <div>Category</div>
        </Col>
        <Col>
          {contactCategoryOptions ? (
            <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
              <Controller
                name='inbound.contact.filters.category_id'
                control={form.control}
                render={({ field }) => (
                  <MultiSelectDropdown
                    name={field.name}
                    options={contactCategoryOptions}
                    onSelectedChanged={field.onChange}
                    initialSelectedOptions={field.value ?? []}
                  />
                )}
              />
            </ScopedBlock>
          ) : null}
        </Col>
      </Row>
      <hr></hr>
      <div className={'label-large'}>Tiers</div>
      <Row className='mb-1 mt-3'>
        <Col className='d-flex align-items-center'>
          <div>Categories</div>
        </Col>
        <Col>
          <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
            <Controller
              name={'inbound.contact.tierCategories'}
              control={form.control}
              render={({ field }) => (
                <MultiSelectDropdown
                  name={field.name}
                  options={tierCategoriesSelectOptions ?? []}
                  initialSelectedOptions={field.value ?? []}
                  onSelectedChanged={field.onChange}
                />
              )}
            />
          </ScopedBlock>
        </Col>
      </Row>
      <Row className='mt-3'>
        <Col>
          <div className='d-flex gap-3 justify-content-end mb-4'>
            <Button
              variant='secondary'
              onClick={handleCancel}
              disabled={isCreateCheckpointLoading || isUpdateCheckpointLoading || isUpdateUserIntegrationLoading}
            >
              Reset
            </Button>
            <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
              <Button
                variant='primary'
                onClick={form.handleSubmit(onSubmit)}
                loading={isCreateCheckpointLoading || isUpdateCheckpointLoading || isUpdateUserIntegrationLoading}
                disabled={isContactStatusesFetching || isContactTypesFetching || isContactCategoriesFetching}
              >
                Save
              </Button>
            </ScopedBlock>
          </div>
        </Col>
      </Row>
    </Container>
  )
}
